import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto.ts';
import {
  GET_USER_CONTRIBUTORS_QUERY_KEY,
  GET_CONTRIBUTOR_SUMMARY_QUERY_KEY,
} from '@/state/queries/contributors/user-contributors.ts';
import type { CreateContributorRoleDto } from '@/types/dtos/contributors/request/create-contributor-role.dto';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';

export const useCreateContributorRoleMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (createContributorRoleData: CreateContributorRoleDto) => {
      const token = await getToken();
      return createContributorRole(createContributorRoleData, token ?? '');
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_USER_CONTRIBUTORS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] }),
        // Invalidate specific contributor summary query
        queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY, variables.contributorId] }),
      ]);
    },
  });
};

const createContributorRole = async (
  createContributorRoleData: CreateContributorRoleDto,
  token: string,
): Promise<ContributorDto> => {
  const { contributorId, role, eventSlotId } = createContributorRoleData;
  let url = `${import.meta.env.VITE_API_URL}/contributors/${contributorId}/roles`;
  if (eventSlotId) {
    url += `?eventSlotId=${encodeURIComponent(eventSlotId)}`;
  }
  return sendJSONRequest({ url, method: METHOD.POST, body: role, token });
};

export default useCreateContributorRoleMutation;
